'use client';
import React, { useState } from 'react';
import Header from '@/components/Header';
import BannerArt from '@/components/BannerArt';
import Footer from '@/components/Footer';
import ScrollRevealInit from '@/app/components/ScrollRevealInit';
import { fireFloodlightPixel, FLOODLIGHT_ACTIVITIES } from '@/lib/floodlight';
import { useFloodlightPageview } from '@/lib/useFloodlightPageview';

interface ContactForm {
  name: string;
  email: string;
  phone: string;
  message: string;
}

// Only set for basePath-deployed builds (the /demo/clients/... static export); empty in production.
const BASE_PATH = process.env.NEXT_PUBLIC_BASE_PATH || '';

const contactDetails = [
  {
    icon: (
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" /><circle cx="12" cy="10" r="3" />
      </svg>
    ),
    label: 'Address',
    value: 'Plot 75, Bhagwanpur Industrial Estate, Bhagwanpur, Bhubaneswar, Khordha, Odisha, 751019.',
    href: null,
  },
  {
    icon: (
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07A19.5 19.5 0 0 1 4.69 13a19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 3.6 2.18h3a2 2 0 0 1 2 1.72c.127.96.361 1.903.7 2.81a2 2 0 0 1-.45 2.11L7.91 9.91a16 16 0 0 0 6.06 6.06l.91-.91a2 2 0 0 1 2.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0 1 22 16.92z" />
      </svg>
    ),
    label: 'Call Us',
    value: '9437694986',
    href: 'tel:9437694986',
  },
];

const socialLinks = [
  {
    label: 'YouTube',
    href: 'https://www.youtube.com/channel/UC5JnFdZjwrI3Rj2zrB6i0DA',
    icon: (
      <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
        <path d="M22.54 6.42a2.78 2.78 0 0 0-1.95-1.96C18.88 4 12 4 12 4s-6.88 0-8.59.46a2.78 2.78 0 0 0-1.95 1.96A29 29 0 0 0 1 12a29 29 0 0 0 .46 5.58A2.78 2.78 0 0 0 3.41 19.6C5.12 20 12 20 12 20s6.88 0 8.59-.46a2.78 2.78 0 0 0 1.95-1.95A29 29 0 0 0 23 12a29 29 0 0 0-.46-5.58z" />
        <polygon points="9.75 15.02 15.5 12 9.75 8.98 9.75 15.02" fill="white" />
      </svg>
    ),
  },
  {
    label: 'Facebook',
    href: 'https://www.facebook.com/kasturichhatua',
    icon: (
      <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
        <path d="M18 2h-3a5 5 0 0 0-5 5v3H7v4h3v8h4v-8h3l1-4h-4V7a1 1 0 0 1 1-1h3z" />
      </svg>
    ),
  },
  {
    label: 'Instagram',
    href: 'https://www.instagram.com/kasturifoodproducts/',
    icon: (
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <rect x="2" y="2" width="20" height="20" rx="5" ry="5" /><path d="M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z" /><line x1="17.5" y1="6.5" x2="17.51" y2="6.5" />
      </svg>
    ),
  },
  {
    label: 'X',
    href: 'https://twitter.com/Kasturi_Foods',
    icon: (
      <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
        <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-4.714-6.231-5.401 6.231H2.744l7.737-8.835L1.254 2.25H8.08l4.259 5.63L18.244 2.25zm-1.161 17.52h1.833L7.084 4.126H5.117L17.083 19.77z" />
      </svg>
    ),
  },
  {
    label: 'LinkedIn',
    href: 'https://www.linkedin.com/company/kasturi-foods/',
    icon: (
      <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
        <path d="M16 8a6 6 0 0 1 6 6v7h-4v-7a2 2 0 0 0-2-2 2 2 0 0 0-2 2v7h-4v-7a6 6 0 0 1 6-6z" /><rect x="2" y="9" width="4" height="12" /><circle cx="4" cy="4" r="2" />
      </svg>
    ),
  },
];

export default function ContactUsClient() {
  useFloodlightPageview(FLOODLIGHT_ACTIVITIES.contactUsPage);

  const [form, setForm] = useState<ContactForm>({ name: '', email: '', phone: '', message: '' });
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setForm((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSubmitting(true);
    try {
      const res = await fetch(`${BASE_PATH}/api/contact`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(form),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => null);
        throw new Error(data?.error || 'Something went wrong. Please try again.');
      }
      setSubmitted(true);
      fireFloodlightPixel(FLOODLIGHT_ACTIVITIES.contactUsFormSubmission);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Something went wrong. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const inputStyle = {
    background: 'var(--card)',
    border: '1px solid var(--border)',
    color: 'var(--foreground)',
    borderRadius: '0.75rem',
    padding: '0.875rem 1rem',
    width: '100%',
    fontSize: '0.875rem',
    outline: 'none',
    transition: 'border-color 0.2s',
  };

  return (
    <main className="grain-overlay bg-background min-h-screen overflow-x-hidden">
      <Header />

      {/* Page Hero */}
      <section
        className="relative pt-32 pb-20 md:pt-40 md:pb-28 overflow-hidden"
        style={{ background: 'linear-gradient(135deg, #2A1015 0%, #3A1820 55%, #2A1015 100%)' }}>
        <BannerArt />
        <div className="max-w-7xl mx-auto px-6 md:px-12 relative z-10">
          <span className="block text-[10px] uppercase tracking-[0.3em] mb-4" style={{ color: 'var(--accent)' }}>
            Reach Out
          </span>
          <h1
            className="font-display font-[200] text-white leading-none mb-6"
            style={{ fontSize: 'clamp(3rem, 8vw, 6rem)', letterSpacing: '0.02em' }}>
            Contact Us
          </h1>
          <p className="text-white/60 text-lg max-w-xl leading-relaxed">
            We would love to hear more from you. Get in touch with us!
          </p>
        </div>
      </section>

      {/* Contact Info + Form */}
      <section className="py-20 md:py-28" style={{ background: 'var(--background)' }}>
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20">
            {/* Left: Contact Details */}
            <div className="reveal reveal-left">
              <span className="block text-[10px] uppercase tracking-[0.25em] mb-5" style={{ color: 'var(--accent-ink)' }}>
                Get In Touch
              </span>
              <h2
                className="font-display font-[200] mb-8"
                style={{ fontSize: 'clamp(1.8rem, 4vw, 2.8rem)', letterSpacing: '0.02em', color: 'var(--foreground)' }}>
                GET IN TOUCH{' '}
                <span className="italic font-light" style={{ color: 'var(--accent-ink)' }}>with us</span>
              </h2>
              <div className="space-y-6 mb-10">
                {contactDetails.map((detail) => (
                  <div key={detail.label} className="flex items-start gap-4">
                    <div
                      className="w-11 h-11 rounded-xl flex items-center justify-center shrink-0 mt-0.5"
                      style={{ background: 'var(--secondary)', color: 'var(--accent-ink)' }}>
                      {detail.icon}
                    </div>
                    <div>
                      <p className="text-xs font-semibold uppercase tracking-wider mb-1" style={{ color: 'var(--muted-foreground)' }}>
                        {detail.label}
                      </p>
                      {detail.href ? (
                        <a
                          href={detail.href}
                          className="text-sm font-medium transition-colors hover:opacity-70"
                          style={{ color: 'var(--foreground)' }}>
                          {detail.value}
                        </a>
                      ) : (
                        <p className="text-sm leading-relaxed" style={{ color: 'var(--foreground)' }}>{detail.value}</p>
                      )}
                    </div>
                  </div>
                ))}
              </div>
              <div>
                <p className="text-xs font-semibold uppercase tracking-wider mb-4" style={{ color: 'var(--muted-foreground)' }}>
                  Follow Us
                </p>
                <div className="flex items-center gap-3">
                  {socialLinks.map((s) => (
                    <a
                      key={s.label}
                      href={s.href}
                      target="_blank"
                      rel="noopener noreferrer"
                      aria-label={s.label}
                      className="w-10 h-10 rounded-xl flex items-center justify-center transition-all duration-200 hover:scale-110"
                      style={{ background: 'var(--secondary)', color: 'var(--accent-ink)' }}
                      onMouseEnter={(e) => { (e.currentTarget as HTMLElement).style.background = 'var(--primary)'; (e.currentTarget as HTMLElement).style.color = 'var(--accent-foreground)'; }}
                      onMouseLeave={(e) => { (e.currentTarget as HTMLElement).style.background = 'var(--secondary)'; (e.currentTarget as HTMLElement).style.color = 'var(--primary)'; }}>
                      {s.icon}
                    </a>
                  ))}
                </div>
              </div>
            </div>

            {/* Right: Feedback Form */}
            <div className="reveal reveal-right">
              <span className="block text-[10px] uppercase tracking-[0.25em] mb-5" style={{ color: 'var(--accent-ink)' }}>
                Feedback
              </span>
              <h2
                className="font-display font-[200] mb-8"
                style={{ fontSize: 'clamp(1.8rem, 4vw, 2.8rem)', letterSpacing: '0.02em', color: 'var(--foreground)' }}>
                Give us your{' '}
                <span className="italic font-light" style={{ color: 'var(--accent-ink)' }}>valuable feedback</span>
              </h2>

              {submitted ? (
                <div
                  className="text-center p-10 rounded-2xl"
                  style={{ background: 'var(--card)', border: '1px solid var(--border)' }}>
                  <div
                    className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-5"
                    style={{ background: 'var(--secondary)', color: 'var(--accent-ink)' }}>
                    <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                      <polyline points="20 6 9 17 4 12" />
                    </svg>
                  </div>
                  <h3 className="font-display font-[200] text-xl mb-2" style={{ color: 'var(--foreground)' }}>Message Sent!</h3>
                  <p className="text-muted-foreground text-sm">Thank you for reaching out. We&apos;ll get back to you shortly.</p>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-5" style={{ background: 'var(--card)', border: '1px solid var(--border)', borderRadius: '1.5rem', padding: '2rem' }}>
                  <div>
                    <label className="block text-xs font-semibold uppercase tracking-wider mb-2" style={{ color: 'var(--muted-foreground)' }}>
                      Name *
                    </label>
                    <input
                      type="text"
                      name="name"
                      required
                      value={form.name}
                      onChange={handleChange}
                      placeholder="Your name"
                      style={inputStyle}
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold uppercase tracking-wider mb-2" style={{ color: 'var(--muted-foreground)' }}>
                      Email *
                    </label>
                    <input
                      type="email"
                      name="email"
                      required
                      value={form.email}
                      onChange={handleChange}
                      placeholder="your@email.com"
                      style={inputStyle}
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold uppercase tracking-wider mb-2" style={{ color: 'var(--muted-foreground)' }}>
                      Phone Number
                    </label>
                    <input
                      type="tel"
                      name="phone"
                      value={form.phone}
                      onChange={handleChange}
                      placeholder="+91 XXXXX XXXXX"
                      style={inputStyle}
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold uppercase tracking-wider mb-2" style={{ color: 'var(--muted-foreground)' }}>
                      Message *
                    </label>
                    <textarea
                      name="message"
                      required
                      value={form.message}
                      onChange={handleChange}
                      placeholder="Your message..."
                      rows={4}
                      style={{ ...inputStyle, resize: 'vertical' }}
                    />
                  </div>
                  {error && (
                    <p className="text-sm" style={{ color: 'var(--highlight)' }}>{error}</p>
                  )}
                  <button
                    type="submit"
                    disabled={submitting}
                    className="w-full py-4 rounded-xl text-sm font-bold uppercase tracking-wider transition-all duration-300 hover:opacity-90 disabled:opacity-60 disabled:cursor-not-allowed"
                    style={{ background: 'var(--accent)', color: 'var(--accent-foreground)' }}>
                    {submitting ? 'Sending...' : 'Send Message'}
                  </button>
                </form>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Map */}
      <section className="pb-20 md:pb-28" style={{ background: 'var(--background)' }}>
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="reveal rounded-2xl overflow-hidden" style={{ height: '400px', border: '1px solid var(--border)' }}>
            <iframe
              src="https://www.google.com/maps/embed?pb=!1m14!1m8!1m3!1d11405.541250320637!2d85.7564096!3d20.2440184!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3a19a62daaaaaaab%3A0x29c6c00b53b33ba6!2sKasturi%20Flour%20Mill%20Private%20Limited!5e1!3m2!1sen!2sin!4v1788519187882!5m2!1sen!2sin"
              width="100%"
              height="100%"
              style={{ border: 0 }}
              allowFullScreen
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              title="Kasturi Food Products location — Bhagwanpur Industrial Estate, Bhubaneswar, Odisha"
            />
          </div>
        </div>
      </section>

      <Footer />
      <ScrollRevealInit />
    </main>
  );
}
